import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { lockSession, logout } from "../../store/authSlice";
import { useTheme } from "../../context/ThemeContext";
import { useUiSecurity } from "../../context/UiSecurityContext";
import { useStoreFeatures } from "../../hooks/useStoreFeatures";
import { useCurrentOperator } from "../../hooks/useCurrentOperator";
import { api, useGetEdgeMenuQuery } from "../../store/api";
import { hasExactRoutePermission } from "../../utils/permissions";
import { buildEdgeMenuAccess } from "../../utils/edgeRbacMenu";
import EdgeMenuAccessGate from "../auth/EdgeMenuAccessGate";
import Modal from "../common/Modal";
import Button from "../common/Button";
import LogoutConfirmationModal from "../common/LogoutConfirmationModal";

const MainLayout = ({ children }) => {
  const sidebarNavRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const savedState = localStorage.getItem("posAdmin.sidebar.open");
    return savedState === null ? true : savedState === "true";
  });
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { requestAccess } = useUiSecurity();
  const {
    features,
    hasFeatures,
    isLoading,
    isError,
  } = useStoreFeatures(true);
  const [posModeModalOpen, setPosModeModalOpen] = useState(false);
  const [accessDeniedModalOpen, setAccessDeniedModalOpen] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState("");
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const { operatorName, operatorInitials } = useCurrentOperator();

  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const isAuthenticated = Boolean(token && user);
  const {
    data: edgeMenu,
    isLoading: edgeMenuLoading,
    isError: edgeMenuError,
  } = useGetEdgeMenuQuery(undefined, {
    skip: !isAuthenticated,
  });
  const edgeMenuAccess = useMemo(
    () => buildEdgeMenuAccess(edgeMenu),
    [edgeMenu],
  );
  const featuresReady = hasFeatures && !isLoading && !isError;
  const isMenuReady = isAuthenticated && !edgeMenuLoading && !edgeMenuError;
  const canUseMenuRbac = isMenuReady && Array.isArray(edgeMenu);
  const canShowMenuRoute = (frontendPath) =>
    hasExactRoutePermission(user, frontendPath) ||
    (!hasExactRoutePermission(user, frontendPath) &&
      !canUseMenuRbac &&
      edgeMenuAccess.canAccessFrontendRoute(frontendPath));
  const canShowSidebarRoute = (frontendPath) => canShowMenuRoute(frontendPath);
  const showPos =
    featuresReady && Boolean(features?.enablePos) && canShowMenuRoute("/pos");
  const showKiosks =
    featuresReady &&
    Boolean(features?.enableKiosk) &&
    canShowMenuRoute("/devices/kiosks");

  const posDineInMode = features?.posDineInMode
    ? String(features.posDineInMode).toUpperCase()
    : "";
  const posEnableTakeaway = features?.posEnableTakeaway !== false;
  const showFloorplanFloors = canShowSidebarRoute("/floorplan/floors");
  const showFloorplanSections = canShowSidebarRoute("/floorplan/sections");
  const showFloorplanTables = canShowSidebarRoute("/floorplan/tables");
  const showCatalogCategories = canShowSidebarRoute("/menu/categories");
  const showCatalogItems = canShowSidebarRoute("/menu/items");
  const showCatalogStations = canShowSidebarRoute("/menu/stations");
  const showCatalogStationAssignment = canShowSidebarRoute(
    "/menu/station-assignment",
  );
  const showUsers = canShowSidebarRoute("/users");
  const showAccessLevels = canShowSidebarRoute("/users/access-levels");
  const showPendingPayments = canShowSidebarRoute(
    "/fulfillment/pending-payments",
  );
  const showDeviceHealth = canShowSidebarRoute("/devices/health");
  const showLocalDevices = canShowSidebarRoute("/devices/local-devices");
  const showDeviceTerminals = canShowSidebarRoute("/devices/terminals");
  const showReportingOrders = canShowSidebarRoute("/reporting/orders");
  const showReportingItems = canShowSidebarRoute("/reporting/items");
  const showTillSessions = canShowSidebarRoute(
    "/cash-management/till-sessions",
  );
  const showCashRefunds = canShowSidebarRoute("/cash-management/refunds");
  const showConfigureStore = canShowSidebarRoute("/settings/configure-store");
  const showTaxRates = canShowSidebarRoute("/settings/tax-rates");
  const showPrinters = canShowSidebarRoute("/settings/printers");
  const showFulfillmentSettings = canShowSidebarRoute("/settings/fulfillment");
  const showFaq = canShowSidebarRoute("/settings/faq");
  const showFloorplanRoutes =
    showFloorplanFloors || showFloorplanSections || showFloorplanTables;
  const canManageFloorplanForQr =
    featuresReady &&
    Boolean(features?.enableQr) &&
    (posDineInMode === "TABLE_DINE_IN" || posDineInMode === "DINE_IN") &&
    showFloorplanRoutes;
  const showFloorplan =
    ((showPos && posDineInMode === "TABLE_DINE_IN") ||
      canManageFloorplanForQr) &&
    showFloorplanRoutes;
  const showCatalogSection =
    showCatalogCategories ||
    showCatalogItems ||
    showCatalogStations ||
    showCatalogStationAssignment;
  const showUserManagementSection = showUsers || showAccessLevels;
  const showFulfillmentSection = showPendingPayments;
  const showDeviceManagementSection =
    showDeviceHealth || showLocalDevices || showKiosks || showDeviceTerminals;
  const showReportingSection = showReportingOrders || showReportingItems;
  const showTillManagementSection =
    showPos && (showTillSessions || showCashRefunds);
  const showSettingsSection =
    showConfigureStore ||
    showTaxRates ||
    showPrinters ||
    showFulfillmentSettings ||
    showFaq;

  useEffect(() => {
    if (location.pathname && location.pathname !== "/not-authorized") {
      localStorage.setItem("posAdmin.lastAllowedPath", location.pathname);
    }
  }, [location.pathname]);

  useEffect(() => {
    const accessDeniedState = location.state?.accessDenied;
    if (!accessDeniedState) return;

    setAccessDeniedMessage(
      accessDeniedState.message ||
        "You do not have access to this feature. If you think this is a mistake, contact an admin.",
    );
    setAccessDeniedModalOpen(true);

    navigate(location.pathname, {
      replace: true,
      state: null,
    });
  }, [location.pathname, location.state, navigate]);

  const posModeOptions = [];
  if (posDineInMode === "TABLE_DINE_IN") {
    posModeOptions.push({
      key: "TABLE_DINE_IN",
      label: "Table Dine-In",
      path: "/pos/table",
    });
  } else if (posDineInMode === "DINE_IN") {
    posModeOptions.push({
      key: "DINE_IN",
      label: "Dine-In",
      path: "/pos/dinein",
    });
  }
  if (posEnableTakeaway) {
    posModeOptions.push({
      key: "TAKEAWAY",
      label: "Takeaway",
      path: "/pos/takeaway",
    });
  }
  const showPosModeButton = showPos && posModeOptions.length > 0;

  const handleLogout = () => {
    setUserMenuOpen(false);
    setLogoutConfirmOpen(true);
  };

  const handleLockSession = () => {
    setUserMenuOpen(false);
    dispatch(lockSession());
    navigate("/session-locked");
  };

  const handleSwitchUser = () => {
    setUserMenuOpen(false);
    dispatch(logout());
    dispatch(api.util.resetApiState());
    navigate("/login");
  };

  const handleOpenPosModeModal = () => {
    if (posModeOptions.length === 1) {
      handleSelectPosMode(posModeOptions[0].path);
      return;
    }
    setPosModeModalOpen(true);
  };

  const handleClosePosModeModal = () => {
    setPosModeModalOpen(false);
  };

  const handleSelectPosMode = (path) => {
    localStorage.removeItem("posAdmin.sidebar.scrollTop");
    window.location.assign(path);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    localStorage.setItem("posAdmin.sidebar.open", String(isSidebarOpen));
  }, [isSidebarOpen]);

  useLayoutEffect(() => {
    const nav = sidebarNavRef.current;
    if (!nav) return;

    const savedScrollTop = Number(
      localStorage.getItem("posAdmin.sidebar.scrollTop"),
    );
    if (Number.isFinite(savedScrollTop) && savedScrollTop >= 0) {
      nav.scrollTop = savedScrollTop;
    }

    const handleScroll = () => {
      localStorage.setItem("posAdmin.sidebar.scrollTop", String(nav.scrollTop));
    };

    nav.addEventListener("scroll", handleScroll);
    return () => {
      localStorage.setItem("posAdmin.sidebar.scrollTop", String(nav.scrollTop));
      nav.removeEventListener("scroll", handleScroll);
    };
  }, [location.pathname, isSidebarOpen]);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, []);

  const isActive = (path) => {
    if (path === "/menu/items") {
      return (
        location.pathname === "/menu/items" ||
        location.pathname.startsWith("/menu/items/")
      );
    }
    return location.pathname === path;
  };

  const getLinkClass = (path) => {
    const baseClass =
      "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition";
    const activeClass =
      "bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-bold shadow-md";
    const inactiveClass =
      "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700";
    return `${baseClass} ${isActive(path) ? activeClass : inactiveClass}`;
  };

  const getSubLinkClass = (active) => {
    const baseClass =
      "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition";
    const activeClass =
      "bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-bold shadow-md";
    const inactiveClass =
      "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700";
    return `${baseClass} ${active ? activeClass : inactiveClass}`;
  };

  const isReportingActive = (path) => {
    if (path === "/reporting/orders") {
      return (
        location.pathname === path ||
        location.pathname.startsWith("/reporting/order-lifecycle/")
      );
    }
    return location.pathname === path;
  };
  const isCashManagementActive = (path, { prefix = false } = {}) =>
    prefix
      ? location.pathname === path || location.pathname.startsWith(`${path}/`)
      : location.pathname === path;

  const handleProtectedNavigation = (sectionKey, path) => (event) => {
    event.preventDefault();
    requestAccess(sectionKey, () => navigate(path));
  };

  return (
    <EdgeMenuAccessGate>
      <div className="flex min-h-screen bg-[var(--color-sand)] text-[var(--color-ink)] transition-colors duration-200">
        {/* Sidebar */}
        <aside
          className={`fixed left-0 top-0 h-full border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 transition-all duration-300 ${
            isSidebarOpen ? "w-56" : "w-0"
          } overflow-hidden`}
        >
          <div className="flex h-full flex-col">
            {/* Sidebar Header */}
            <div className="border-b border-slate-200 dark:border-slate-700 px-6 py-3.5">
              <span className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                POS Admin
              </span>
            </div>

            {/* Sidebar Navigation */}
            <nav
              ref={sidebarNavRef}
              className="flex-1 space-y-1 overflow-y-auto px-3 py-3.5"
            >
              {/* Floorplan Section */}
              {showFloorplan && (
                <div className="mb-4">
                  <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    Floorplan
                  </div>
                  <div className="space-y-1">
                    {showFloorplanFloors && (
                      <Link
                        to="/floorplan/floors"
                        className={getLinkClass("/floorplan/floors")}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                        Floors
                      </Link>
                    )}

                    {showFloorplanSections && (
                      <Link
                        to="/floorplan/sections"
                        className={getLinkClass("/floorplan/sections")}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z"
                          />
                        </svg>
                        Sections
                      </Link>
                    )}

                    {showFloorplanTables && (
                      <Link
                        to="/floorplan/tables"
                        className={getLinkClass("/floorplan/tables")}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                          />
                        </svg>
                        Tables
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {/* Catalog Section */}
              {showCatalogSection && (
                <div className="mb-4">
                  <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    Catalog
                  </div>
                  <div className="space-y-1">
                    {showCatalogCategories && (
                      <Link
                        to="/menu/categories"
                        className={getLinkClass("/menu/categories")}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 6h16M4 10h16M4 14h16M4 18h16"
                          />
                        </svg>
                        Categories
                      </Link>
                    )}

                    {showCatalogItems && (
                      <Link
                        to="/menu/items"
                        className={getLinkClass("/menu/items")}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                        Items
                      </Link>
                    )}

                    {showCatalogStations && (
                      <Link
                        to="/menu/stations"
                        className={getLinkClass("/menu/stations")}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 7h18M3 12h18M3 17h18"
                          />
                        </svg>
                        Stations
                      </Link>
                    )}

                    {showCatalogStationAssignment && (
                      <Link
                        to="/menu/station-assignment"
                        className={getLinkClass("/menu/station-assignment")}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 7h10M7 12h6m-6 5h10M17 9l2-2m0 0l-2-2m2 2h-4m-8 8l-2 2m0 0l2 2m-2-2h4"
                          />
                        </svg>
                        Station Assignment
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {/* Role & User Section */}
              {showUserManagementSection && (
                <div className="mb-4">
                  <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    Role & User
                  </div>
                  <div className="space-y-1">
                    {showUsers && (
                      <Link
                        to="/users"
                        className={getLinkClass("/users")}
                        onClick={handleProtectedNavigation("users", "/users")}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                          />
                        </svg>
                        Users
                      </Link>
                    )}

                    {showAccessLevels && (
                      <Link
                        to="/users/access-levels"
                        className={getLinkClass("/users/access-levels")}
                        onClick={handleProtectedNavigation(
                          "users",
                          "/users/access-levels",
                        )}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                          />
                        </svg>
                        Access Levels
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {/* Fulfillment Section */}
              {showFulfillmentSection && (
                <div className="mb-4">
                  <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    Fulfillment
                  </div>
                  <div className="space-y-1">
                    {showPendingPayments && (
                      <Link
                        to="/fulfillment/pending-payments"
                        className={getLinkClass(
                          "/fulfillment/pending-payments",
                        )}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-10V6m0 12v-2m9-4a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Pending Payments
                      </Link>
                    )}
                    {/* <Link
                  to="/fulfillment/pos-orders"
                  className={getLinkClass("/fulfillment/pos-orders")}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V9m-7-4h6m-6 4h6M9 13h3m-3 4h6"
                    />
                  </svg>
                  POS Orders
                </Link> */}
                  </div>
                </div>
              )}

              {/* Device Management Section */}
              {showDeviceManagementSection && (
                <div className="mb-4">
                  <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    Device Management
                  </div>
                  <div className="space-y-1">
                    {showDeviceHealth && (
                      <Link
                        to="/devices/health"
                        className={getLinkClass("/devices/health")}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 12h4l3-8 4 16 3-8h4"
                          />
                        </svg>
                        Device Health
                      </Link>
                    )}

                    {showLocalDevices && (
                      <Link
                        to="/devices/local-devices"
                        className={getLinkClass("/devices/local-devices")}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        Local Devices
                      </Link>
                    )}

                    {showKiosks && (
                      <Link
                        to="/devices/kiosks"
                        className={getLinkClass("/devices/kiosks")}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                          />
                        </svg>
                        Kiosks
                      </Link>
                    )}

                    {showDeviceTerminals && (
                      <Link
                        to="/devices/terminals"
                        className={getLinkClass("/devices/terminals")}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                          />
                        </svg>
                        Terminals
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {/* Reporting Section */}
              {showReportingSection && (
                <div className="mb-4">
                  <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    Reporting
                  </div>
                  <div className="space-y-1">
                    {showReportingOrders && (
                      <Link
                        to="/reporting/orders"
                        className={getSubLinkClass(
                          isReportingActive("/reporting/orders"),
                        )}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 3v18h18M7 13l3-3 4 4 5-6"
                          />
                        </svg>
                        Orders
                      </Link>
                    )}

                    {showReportingItems && (
                      <Link
                        to="/reporting/items"
                        className={getSubLinkClass(
                          isReportingActive("/reporting/items"),
                        )}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 6h16M4 10h16M4 14h16M4 18h10"
                          />
                        </svg>
                        Items
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {/* Till Management Section */}
              {showTillManagementSection && (
                <div className="mb-4">
                  <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    Till Management
                  </div>
                  <div className="space-y-1">
                    {/*
                    Tills are hidden from the sidebar because POS setup now
                    uses Local Devices plus active Till Sessions.
                    */}
                    {showTillSessions && (
                      <Link
                        to="/cash-management/till-sessions"
                        className={getSubLinkClass(
                          isCashManagementActive(
                            "/cash-management/till-sessions",
                            {
                              prefix: true,
                            },
                          ),
                        )}
                        onClick={handleProtectedNavigation(
                          "tillSessions",
                          "/cash-management/till-sessions",
                        )}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-10V6m0 12v-2m9-4a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Till Sessions
                      </Link>
                    )}
                    {showCashRefunds && (
                      <Link
                        to="/cash-management/refunds"
                        className={getSubLinkClass(
                          isCashManagementActive("/cash-management/refunds"),
                        )}
                        onClick={handleProtectedNavigation(
                          "cashRefunds",
                          "/cash-management/refunds",
                        )}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7zM7 11h10M8 15h2"
                          />
                        </svg>
                        Refunds
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {/* Upselling Section - Deprecated in Edge; kept commented for reference */}
              {/*
            <div className="mb-4">
              <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                Upselling
              </div>
              <div className="space-y-1">
                <Link
                  to="/upselling/campaigns"
                  className={getLinkClass("/upselling/campaigns")}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                  Campaigns
                </Link>
              </div>
            </div>
            */}

              {/* Settings Section */}
              {showSettingsSection && (
                <div className="mb-4">
                  <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    Settings
                  </div>
                  <div className="space-y-1">
                    {showConfigureStore && (
                      <Link
                        to="/settings/configure-store"
                        className={getLinkClass("/settings/configure-store")}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 12.414A8 8 0 1112.414 13.414l4.243 4.243a1 1 0 01-1.414 1.414z"
                          />
                        </svg>
                        Configure Store
                      </Link>
                    )}
                    {showTaxRates && (
                      <Link
                        to="/settings/tax-rates"
                        className={getLinkClass("/settings/tax-rates")}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Tax Rates
                      </Link>
                    )}
                    {showPrinters && (
                      <Link
                        to="/settings/printers"
                        className={getLinkClass("/settings/printers")}
                        onClick={handleProtectedNavigation(
                          "printers",
                          "/settings/printers",
                        )}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 9V4h12v5M6 17H5a2 2 0 01-2-2v-4a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2h-1M6 17h12v3H6v-3z"
                          />
                        </svg>
                        Printers
                      </Link>
                    )}
                    {showFulfillmentSettings && (
                      <Link
                        to="/settings/fulfillment"
                        className={getLinkClass("/settings/fulfillment")}
                        onClick={handleProtectedNavigation(
                          "generalSettings",
                          "/settings/fulfillment",
                        )}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        General Settings
                      </Link>
                    )}
                    {showFaq && (
                      <Link
                        to="/settings/faq"
                        className={getLinkClass("/settings/faq")}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.219-.969 2.287-2.36 2.746-.83.274-1.64.87-1.64 1.754V16m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z"
                          />
                        </svg>
                        FAQ
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </nav>

          {/* Sidebar Footer */}
          <div className="border-t border-slate-200 dark:border-slate-700 px-3 py-3">
            <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 dark:border-slate-700 dark:bg-slate-800/70">
              <div
                className="flex min-w-0 flex-1 items-center gap-2"
                title={`Logged in as ${operatorName}`}
              >
                <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[9px] font-black text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200">
                  {operatorInitials}
                </span>
                <div className="min-w-0 leading-tight">
                  <div className="truncate text-[11px] font-semibold text-slate-900 dark:text-slate-100">
                    {operatorName}
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-rose-600 transition hover:bg-rose-50 hover:text-rose-700 dark:text-rose-400 dark:hover:bg-rose-950/30 dark:hover:text-rose-300"
                onClick={handleLogout}
                title="Log out"
                aria-label="Log out"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
        <div
          className={`flex min-h-screen flex-1 flex-col bg-[var(--color-sand)] transition-all duration-300 ${
            isSidebarOpen ? "ml-56" : "ml-0"
          }`}
        >
          {/* Top Bar */}
          <div className="sticky top-0 z-30 border-b border-slate-200 bg-white/70 backdrop-blur dark:border-slate-700 dark:bg-slate-800/70">
            <div className="flex items-center justify-between px-4 py-2">
              <button
                type="button"
                onClick={toggleSidebar}
                className="rounded-lg p-2 text-slate-600 dark:text-slate-400 transition hover:bg-slate-100 dark:hover:bg-slate-700"
                aria-label="Toggle sidebar"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>

              {/* User Info and Actions */}
              <div className="flex items-center gap-3">
                {/* Dark Mode Toggle */}
                <button
                  type="button"
                  onClick={toggleDarkMode}
                  className="rounded-lg p-2 text-slate-600 dark:text-slate-400 transition hover:bg-slate-100 dark:hover:bg-slate-700"
                  aria-label="Toggle dark mode"
                >
                  {isDarkMode ? (
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                      />
                    </svg>
                  )}
                </button>

                {showPosModeButton && (
                  <Button
                    type="button"
                    size="sm"
                    variant="pos-primary"
                    onClick={handleOpenPosModeModal}
                    className="inline-flex items-center gap-2 whitespace-nowrap"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 0v10"
                      />
                    </svg>
                    POS Mode
                  </Button>
                )}

              {/* <div className="flex items-center gap-2 text-sm">
                <div className="relative" ref={userMenuRef}>
                  <button
                    type="button"
                    onClick={() => setUserMenuOpen((open) => !open)}
                    className="inline-flex max-w-[190px] items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-2.5 py-1 shadow-sm backdrop-blur transition hover:border-slate-300 hover:bg-white dark:border-slate-700 dark:bg-slate-800/80 dark:hover:border-slate-600 dark:hover:bg-slate-800"
                    title={`Logged in as ${operatorName}`}
                    aria-haspopup="menu"
                    aria-expanded={userMenuOpen}
                  >
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-[10px] font-black text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200">
                      {operatorInitials}
                    </span>
                    <span className="truncate text-[11px] font-semibold text-slate-700 dark:text-slate-200">
                      {operatorName}
                    </span>
                    <svg
                      className={`h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform duration-150 ${
                        userMenuOpen ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 top-full z-50 mt-2 w-44 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl dark:border-slate-700 dark:bg-slate-900">
                      <button
                        type="button"
                        onClick={handleLockSession}
                        className="mb-1 flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                      >
                        <span>Lock</span>
                        <svg
                          className="h-4 w-4 shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={handleSwitchUser}
                        className="mb-1 flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                      >
                        <span>Switch user</span>
                        <svg
                          className="h-4 w-4 shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-rose-600 transition hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-950/30"
                      >
                        <span>Logout</span>
                        <svg
                          className="h-4 w-4 shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>

                {/* <div className="flex items-center gap-2 text-sm">
                <svg
                  className="h-5 w-5 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                <span className="font-medium text-slate-700">
                  {user?.accessLevel || "User"}
                </span>
              </div> */}
                {/* <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                Active
              </span> */}
              </div>
            </div>
          </div>

          {/* Page Content */}
          <div className="flex-1 px-3 py-3 md:px-4 md:py-4">{children}</div>
        </div>

        <Modal
          isOpen={posModeModalOpen}
          onClose={handleClosePosModeModal}
          title="Select POS Mode"
          showCancel={false}
        >
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Choose what you want to start with.
            </p>
            <div
              className={`grid gap-3 ${
                posModeOptions.length > 1 ? "grid-cols-2" : "grid-cols-1"
              }`}
            >
              {posModeOptions.map((option) => (
                <Button
                  key={option.key}
                  type="button"
                  variant="secondary"
                  onClick={() => handleSelectPosMode(option.path)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={accessDeniedModalOpen}
          onClose={() => setAccessDeniedModalOpen(false)}
          title="Access Denied"
          showCancel={false}
        >
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {accessDeniedMessage ||
                "You do not have access to this feature. If you think this is a mistake, contact an admin."}
            </p>
            <div className="flex justify-end">
              <Button
                type="button"
                onClick={() => setAccessDeniedModalOpen(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </Modal>

        <LogoutConfirmationModal
          isOpen={logoutConfirmOpen}
          onClose={() => setLogoutConfirmOpen(false)}
        />
      </div>
    </EdgeMenuAccessGate>
  );
};

export default MainLayout;
